import { Col, Row } from "antd";
import { LayoutGrid, Package, Tag } from "lucide-react";
import { isAdminOrSuperAdmin } from "utility/helper";
import type { DashboardOverviewResponse } from "../types";
import { DailyRevenueStatCard } from "./DailyRevenueStatCard";
import { DashboardStatCard } from "./DashboardStatCard";

const ico = { size: 24, strokeWidth: 1.75, color: "rgba(0,0,0,0.45)" } as const;

const TONE_UP = "#389e0d";
const TONE_FLAT = "rgba(0,0,0,0.45)";

function formatProductDelta(n: number): { text: string; tone: string } {
  if (n > 0) {
    return { text: `+${n} nya denna månad`, tone: TONE_UP };
  }
  return { text: "Oförändrat", tone: TONE_FLAT };
}

function formatSinceLastMonthDelta(n: number): { text: string; tone: string } {
  if (n > 0) {
    return { text: `+${n} sedan förra månaden`, tone: TONE_UP };
  }
  return { text: "Oförändrat", tone: TONE_FLAT };
}

export type DashboardStatCardsProps = {
  data?: DashboardOverviewResponse | null;
  loading?: boolean;
};

export function DashboardStatCards({ data, loading = false }: DashboardStatCardsProps) {
  const showDailyRevenue = isAdminOrSuperAdmin();
  const colSpan = showDailyRevenue ? { xs: 24, sm: 12, xl: 6 } : { xs: 24, sm: 12, xl: 8 };

  const productsDelta = formatProductDelta(data?.products.createdThisMonth ?? 0);
  const categoriesDelta = formatSinceLastMonthDelta(
    data?.categories.createdThisMonth ?? 0,
  );
  const brandsDelta = formatSinceLastMonthDelta(data?.brands.createdThisMonth ?? 0);

  const stats = [
    {
      label: "Produkter",
      value: data?.products.total ?? 0,
      icon: <Package {...ico} />,
      delta: productsDelta.text,
      tone: productsDelta.tone,
    },
    {
      label: "Kategorier",
      value: data?.categories.total ?? 0,
      icon: <LayoutGrid {...ico} />,
      delta: categoriesDelta.text,
      tone: categoriesDelta.tone,
    },
    {
      label: "Varumärken",
      value: data?.brands.total ?? 0,
      icon: <Tag {...ico} />,
      delta: brandsDelta.text,
      tone: brandsDelta.tone,
    },
  ] as const;

  return (
    <Row gutter={[16, 16]} align="stretch" style={{ marginBottom: 24 }}>
      {showDailyRevenue && (
        <Col {...colSpan} style={{ display: "flex" }}>
          <DailyRevenueStatCard />
        </Col>
      )}
      {stats.map((stat) => (
        <Col key={stat.label} {...colSpan} style={{ display: "flex" }}>
          <DashboardStatCard
            label={stat.label}
            value={stat.value}
            icon={stat.icon}
            delta={stat.delta}
            tone={stat.tone}
            loading={loading}
          />
        </Col>
      ))}
    </Row>
  );
}
